import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { PendingKeywordFile } from "@/hooks/useKeywordVolumeImport";

interface ConfirmM1MonthProps {
  files: PendingKeywordFile[];
  loading: boolean;
  /** Confirmed M1 per file ("YYYY-MM-01"), same order as files; "" for skipped. */
  onConfirm: (months: string[]) => void;
  onCancel: () => void;
}

/** Render with a key that changes per selection so each starts from its suggestions. */
export function ConfirmM1Month({ files, loading, onConfirm, onCancel }: ConfirmM1MonthProps) {
  const [months, setMonths] = useState(files.map((f) => f.suggestedM1.slice(0, 7)));
  const readable = files.filter((f) => !f.error);
  const ready = files.every((f, i) => f.error || months[i]);

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>M1 month, found automatically</CardTitle>
        <CardDescription>
          Found from the numbers: the same keyword's monthly values line up, shifted, with data already
          stored or with the other files here. The newest export with nothing stored before it
          takes its month from the export date; a file whose pairs disagree is marked. Change a month if you know better; order does not matter.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {files.map((f, i) => (
          <div key={`${f.fileName}-${i}`} className="flex flex-wrap items-center gap-3 text-sm">
            <span className="w-56 truncate font-medium" title={f.fileName}>{f.fileName}</span>
            {f.error ? (
              <span className="signal-text-negative">skipped: {f.error}</span>
            ) : (
              <>
                <span className="w-24 text-muted-foreground">{f.rows.length.toLocaleString("en-US")} rows</span>
                <label className="text-muted-foreground" htmlFor={`kv-m1-${i}`}>M1 =</label>
                <Input
                  id={`kv-m1-${i}`}
                  type="month"
                  className="w-44"
                  value={months[i]}
                  onChange={(e) => setMonths((m) => m.map((v, j) => (j === i ? e.target.value : v)))}
                />
<span className="text-xs text-muted-foreground">{f.detail}</span>
                {f.conflict ? <span className="text-xs signal-text-warning">check: {f.conflict}</span> : null}
              </>
            )}
          </div>
        ))}
        <div className="flex gap-2 pt-2">
          <Button
            disabled={loading || !readable.length || !ready}
            onClick={() => onConfirm(files.map((f, i) => (f.error || !months[i] ? "" : `${months[i]}-01`)))}
          >
            Upload {readable.length} {readable.length === 1 ? "file" : "files"}
          </Button>
          <Button variant="ghost" disabled={loading} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
